import React, { useMemo } from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import useResponsive from "../../hooks/useResponsive";
import { CHART_COLORS } from "@/constants/chart-colors";

const CASH_FLOW_COLORS = {
  revenue: CHART_COLORS.revenue,
  expenses: CHART_COLORS.expense,
  net_positive: CHART_COLORS.cashPositive,
  net_negative: CHART_COLORS.cashNegative,
};

interface CashFlowHealthChartProps {
  currentSnapshot?: {
    monthlyRevenue: number;
    monthlyBurn: number;
    netMonthlyChange: number;
    cashBalance: number;
  } | null;
  currentDate?: Date;
}

const CashFlowHealthChart: React.FC<CashFlowHealthChartProps> = ({
  currentSnapshot,
  currentDate = new Date(),
}) => {
  const { isMobile, isTablet } = useResponsive();

  // Responsive chart sizing
  const chartConfig = useMemo(() => {
    if (isMobile) {
      return {
        cx: "50%",
        cy: "45%",
        innerRadius: 20,
        outerRadius: 50,
        legendHeight: 60,
        showLegend: false,
      };
    } else if (isTablet) {
      return {
        cx: "50%",
        cy: "42%",
        innerRadius: 22,
        outerRadius: 55,
        legendHeight: 50,
        showLegend: true,
      };
    } else {
      return {
        cx: "50%",
        cy: "40%",
        innerRadius: 25,
        outerRadius: 60,
        legendHeight: 40,
        showLegend: true,
      };
    }
  }, [isMobile, isTablet]);

  const chartData = useMemo(() => {
    if (!currentSnapshot) return [];

    const revenue = currentSnapshot.monthlyRevenue || 0;
    const expenses = Math.abs(currentSnapshot.monthlyBurn || 0);

    // Show the cash flow as incoming vs outgoing
    const data = [];

    if (revenue > 0) {
      data.push({
        name: "Incoming Cash",
        value: revenue,
        color: CASH_FLOW_COLORS.revenue,
      });
    }

    if (expenses > 0) {
      data.push({
        name: "Outgoing Cash",
        value: expenses,
        color: CASH_FLOW_COLORS.expenses,
      });
    }

    return data.sort((a, b) => b.value - a.value);
  }, [currentSnapshot]);

  const totalCashFlow = chartData.reduce((sum, item) => sum + item.value, 0);
  const netFlow = currentSnapshot ? currentSnapshot.netMonthlyChange || 0 : 0;

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const percentage =
        totalCashFlow > 0
          ? ((data.value / totalCashFlow) * 100).toFixed(1)
          : "0";

      return (
        <div className="bg-surface-elevated text-white text-xs px-3 py-2 rounded-lg shadow-glow">
          <p className="font-semibold">{data.payload.name}</p>
          <p
            className={`${
              data.payload.name === "Incoming Cash"
                ? "text-brand"
                : "text-expense"
            }`}
          >
            ${data.value.toLocaleString()}/month
          </p>
          <p className="text-white/55">{percentage}% of total flow</p>
        </div>
      );
    }
    return null;
  };

  if (chartData.length === 0) {
    return (
      <div className="bg-surface-elevated rounded-lg border h-full flex flex-col">
        <div className="px-4 py-3 border-b bg-white/[0.06] flex-shrink-0">
          <h4 className="text-sm font-medium text-white/75">
            Cash Flow Health
          </h4>
          <p className="text-xs text-white/45 mt-1">No cash flow data</p>
        </div>
        <div className="p-4 flex-1 flex items-center justify-center">
          <p className="text-white/45 text-sm">No cash flow data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface-elevated rounded-lg border h-full flex flex-col">
      <div className="px-4 py-3 border-b bg-white/[0.06] flex-shrink-0">
        <h4 className="text-sm font-medium text-white/75">Cash Flow Health</h4>
        <p className="text-xs text-white/45 mt-1">
          {currentDate.toLocaleDateString("en-US", {
            month: "short",
            year: "numeric",
          })}
          : ${Math.abs(netFlow).toLocaleString()}/month{" "}
          {netFlow >= 0 ? "surplus" : "deficit"}
        </p>
      </div>

      <div className="p-4 flex-1 min-h-0 pb-6">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx={chartConfig.cx}
              cy={chartConfig.cy}
              innerRadius={chartConfig.innerRadius}
              outerRadius={chartConfig.outerRadius}
              paddingAngle={2}
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            {chartConfig.showLegend && (
              <Legend
                verticalAlign="bottom"
                height={chartConfig.legendHeight}
                formatter={(value, entry) => {
                  if (isMobile) {
                    return value.split(" ")[0];
                  }
                  return `${value} ($${(
                    entry.payload?.value || 0
                  ).toLocaleString()})`;
                }}
                wrapperStyle={{
                  fontSize: isMobile ? "9px" : "10px",
                  lineHeight: "1.2",
                  paddingTop: "4px",
                  paddingBottom: "8px",
                }}
              />
            )}
          </PieChart>
        </ResponsiveContainer>

        {/* Mobile Legend Alternative */}
        {isMobile && chartData.length > 0 && (
          <div className="mt-2 space-y-1">
            {chartData.map((entry, index) => (
              <div
                key={index}
                className="flex items-center justify-between text-xs"
              >
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className="text-white/75">{entry.name}</span>
                </div>
                <span className="text-white/65 font-medium">
                  ${entry.value.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CashFlowHealthChart;
